<template>
  <div class="page">
    <!-- Company and theme share the top row: on a phone every row costs
         scroll, and both are things you set once and forget. -->
    <div class="appbar">
      <select
          :value="companyId"
          aria-label="Company"
          @change="$router.push({ name: 'TruckList', params: { companyId: $event.target.value } })"
      >
        <option v-for="c in companies" :key="c.id" :value="c.id">{{ c.name }}</option>
      </select>
      <ThemeToggle />
    </div>

    <div class="controls">
      <select v-model="filters.status" aria-label="Filter by duty status">
        <option value="all">All statuses</option>
        <option value="driving">Driving</option>
        <option value="online">On duty</option>
        <option value="idle">Sleeper</option>
        <option value="offline">Off duty</option>
        <option value="pc">Personal conveyance</option>
        <option value="ym">Yard move</option>
      </select>

      <select v-model="filters.sort" aria-label="Sort order">
        <option value="asc">Unit ↑</option>
        <option value="desc">Unit ↓</option>
      </select>
    </div>

    <div v-if="isFirstLoad" class="state-note">Loading trucks…</div>
    <div v-else-if="error" class="state-note error">{{ error }}</div>
    <div v-else class="result-count">{{ countLabel }}</div>

    <ul class="list">
      <li v-for="t in sortedAndFiltered" :key="t.id" class="card">
        <div class="row">
          <div class="unit">
            <i class="fa-solid fa-truck unit-icon" aria-hidden="true"></i>
            <span class="num">{{ t.number }}</span>
            <!-- Speed only when it is news; the pill below carries the state. -->
            <span v-if="t.speed > 0" class="speed">{{ t.speed }} mph</span>
          </div>

          <a
              v-if="t.liveShareLink"
              :href="t.liveShareLink"
              target="_blank"
              rel="noopener noreferrer"
              class="icon-btn"
              :aria-label="`Open live location for unit ${t.number}`"
              :title="`Live location for unit ${t.number}`"
          >
            <i class="fa-solid fa-location-crosshairs"></i>
          </a>
        </div>

        <!-- Location wraps rather than truncating: at this type size on a phone
             an ellipsis eats the town name, which is the point of the line. -->
        <div class="row small row-top">
          <span class="loc">
            <i class="fa-solid fa-location-dot"></i>
            <span class="loc-text">{{ t.location || 'Location unknown' }}</span>
          </span>
          <span
              :class="['time', { stale: isStale(t) }]"
              :title="isStale(t) ? 'No report in over 12 hours' : null"
          >{{ t.lastSeen }}</span>
        </div>

        <div class="row small">
          <div class="unit">
            <span :class="['pill', pillClass(t)]">{{ t.statusShort }}</span>
            <span class="driver">{{ t.driver || 'Unassigned' }}</span>
          </div>
        </div>

        <button
            class="btn-diagnostics btn-block"
            :aria-expanded="String(expandedTruck === t.id)"
            @click="toggleDiagnostics(t.id, t.driverId)"
        >
          {{ expandedTruck === t.id ? 'Hide diagnostics' : 'Diagnostics' }}
        </button>

        <div v-if="expandedTruck === t.id" class="diagnostics-panel">
          <div v-if="diagnosticsLoading" class="diag-note">Loading…</div>

          <template v-else>
            <div v-if="!diagnosticsData[t.id]" class="diag-note error">
              Diagnostics unavailable for this unit.
            </div>
            <template v-else>
              <div class="diag-row">
                <span>Engine state</span>
                <span>{{ diagnosticsData[t.id].connectionStatus ?? '—' }}</span>
              </div>
              <div class="diag-row">
                <span>Engine hours</span>
                <span>{{ diagnosticsData[t.id].engineHours ?? '—' }}</span>
              </div>
              <div class="diag-row">
                <span>Odometer</span>
                <span>{{ diagnosticsData[t.id].odometer != null ? diagnosticsData[t.id].odometer + ' mi' : '—' }}</span>
              </div>
              <div class="diag-row">
                <span>Engine speed</span>
                <span>{{ diagnosticsData[t.id].rpm != null ? diagnosticsData[t.id].rpm + ' rpm' : '—' }}</span>
              </div>
              <div class="diag-row">
                <span>Fuel level</span>
                <div class="diag-bar-group">
                  <div class="diag-bar">
                    <div class="diag-bar-fill" :style="{ width: barWidth(diagnosticsData[t.id].fuelLevel) }"></div>
                  </div>
                  <span>{{ diagnosticsData[t.id].fuelLevel != null ? diagnosticsData[t.id].fuelLevel + '%' : '—' }}</span>
                </div>
              </div>
              <div class="diag-row">
                <span>DEF level</span>
                <div class="diag-bar-group">
                  <div class="diag-bar">
                    <div class="diag-bar-fill" :style="{ width: barWidth(diagnosticsData[t.id].defLevel) }"></div>
                  </div>
                  <span>{{ diagnosticsData[t.id].defLevel != null ? diagnosticsData[t.id].defLevel + '%' : '—' }}</span>
                </div>
              </div>
            </template>

            <!-- Outside the block above on purpose: hours of service come from a
                 separate call, so they still show when diagnostics fail. -->
            <div class="hos-grid">
              <HosDial
                  v-for="clock in hosClocks(t.driverId)"
                  :key="clock.label"
                  :label="clock.label"
                  :time="clock.time"
                  :progress="clock.progress"
                  :minutes-left="clock.minutesLeft"
                  :color="clock.color"
              />
            </div>
          </template>
        </div>
      </li>
    </ul>

    <div v-if="!isFirstLoad && !error && sortedAndFiltered.length === 0" class="state-note">
      No trucks match this filter.
    </div>
  </div>
</template>

<script>
import { driverViewById, fetchCompanies, fetchTrucks, fetchVehicleState } from '@/services/ApiService'
import HosDial from './HosDial.vue'
import ThemeToggle from './ThemeToggle.vue'

/** A device that has not reported in this long is worth noticing. */
const STALE_AFTER_MS = 12 * 60 * 60 * 1000

export default {
  name: 'TruckList',
  components: { HosDial, ThemeToggle },
  props: {
    companyId: { type: String, required: true },
  },
  data() {
    return {
      filters: { status: 'all', sort: 'asc' },
      items: [],
      companies: [],
      error: null,
      refreshTimer: null,
      isFirstLoad: true,
      expandedTruck: null,
      diagnosticsData: {},
      driverDetail: {},
      diagnosticsLoading: false,
    }
  },
  computed: {
    sortedAndFiltered() {
      const arr = this.filters.status === 'all'
          ? this.items
          : this.items.filter((t) => t.state === this.filters.status)

      return [...arr].sort((a, b) => {
        const an = a.number.toString().padStart(6, '0')
        const bn = b.number.toString().padStart(6, '0')
        return this.filters.sort === 'asc' ? an.localeCompare(bn) : bn.localeCompare(an)
      })
    },
    countLabel() {
      const shown = this.sortedAndFiltered.length
      const total = this.items.length
      const noun = total === 1 ? 'truck' : 'trucks'
      return shown === total ? `${total} ${noun}` : `${shown} of ${total} ${noun}`
    },
  },
  methods: {
    async loadTrucks() {
      try {
        this.items = await fetchTrucks(this.companyId)
        this.error = null
      } catch (e) {
        this.error = e?.message || 'Could not load trucks.'
      } finally {
        this.isFirstLoad = false
      }
    },
    async loadCompanies() {
      try {
        this.companies = await fetchCompanies()
      } catch {
        this.companies = []
      }
    },
    async toggleDiagnostics(truckId, driverId) {
      if (this.expandedTruck === truckId) {
        this.expandedTruck = null
        return
      }

      this.expandedTruck = truckId
      this.diagnosticsLoading = true
      await this.loadDriverDetail(driverId)

      try {
        const state = await fetchVehicleState(truckId, this.companyId)
        this.diagnosticsData = { ...this.diagnosticsData, [truckId]: state }
      } catch {
        this.diagnosticsData = { ...this.diagnosticsData, [truckId]: null }
      } finally {
        this.diagnosticsLoading = false
      }
    },
    async loadDriverDetail(driverId) {
      try {
        const state = await driverViewById(driverId, this.companyId)
        this.driverDetail = { ...this.driverDetail, [driverId]: state }
      } catch {
        this.driverDetail = { ...this.driverDetail, [driverId]: null }
      }
    },
    /** Break and Drive first: they are the clocks that run out soonest. */
    hosClocks(driverId) {
      const detail = this.driverDetail[driverId]
      const spec = [
        { label: 'Break', key: 'breakTime', color: 'var(--hos-break)' },
        { label: 'Drive', key: 'driveTime', color: 'var(--hos-drive)' },
        { label: 'Shift', key: 'shiftTime', color: 'var(--hos-shift)' },
        { label: 'Cycle', key: 'cycleTime', color: 'var(--hos-cycle)' },
      ]

      return spec.map(({ label, key, color }) => {
        const clock = detail ? detail[key] : null
        return {
          label,
          color,
          time: clock ? clock.remainingFormatted : '—',
          progress: clock ? clock.remainingPercent : 0,
          minutesLeft: clock ? clock.remainingMinutes : null,
        }
      })
    },
    barWidth(value) {
      const n = Number(value)
      if (!Number.isFinite(n)) return '0%'
      return `${Math.min(Math.max(n, 0), 100)}%`
    },
    isStale(t) {
      if (!t.lastSeenAt) return false
      const seen = new Date(t.lastSeenAt).getTime()
      if (!Number.isFinite(seen)) return false
      return Date.now() - seen > STALE_AFTER_MS
    },
    pillClass(t) {
      return {
        D: 'pill-driving',
        ON: 'pill-on',
        OFF: 'pill-off',
        SB: 'pill-sleep',
        PC: 'pill-pc',
        YM: 'pill-ym',
      }[t.statusShort] || 'pill-off'
    },
  },
  mounted() {
    this.loadCompanies()
    this.loadTrucks()
    this.refreshTimer = setInterval(() => this.loadTrucks(), 10000)
  },
  beforeUnmount() {
    if (this.refreshTimer) clearInterval(this.refreshTimer)
  },
  watch: {
    companyId() {
      this.items = []
      this.isFirstLoad = true
      this.expandedTruck = null
      this.loadTrucks()
    },
  },
}
</script>

<style scoped>
@import './TruckList.css';
</style>
